import '../styles/app.css';

import { initSettingsPage } from './pages/settings.js';
import { initTour } from './lib/tour.js';
import { initWelcomePage } from './pages/welcome.js';
import { initReactionsListPage, initReactionsEditorPage } from './pages/reactions.js';
import { initTicketsListPage, initTicketsEditorPage } from './pages/tickets.js';
import { initCustomBotPage } from './pages/custom-bot.js';
import { initBillingPage } from './pages/billing.js';
import { initLogsPage } from './pages/logs.js';
import { initLevelsPage } from './pages/levels.js';
import { initSectionNav } from './lib/section-nav.js';
import { initModerationPage } from './pages/moderation.js';
import { initAutoModPage } from './pages/automod.js';
import { initRolesPage } from './pages/roles.js';
import { initColorsPage } from './pages/colors.js';
import { initAutomationsPage } from './pages/automations.js';
import { initReputationPage } from './pages/reputation.js';
import { initEconomyPage } from './pages/economy.js';
import { initBackupsPage } from './pages/backups.js';
import { initEmbedsListPage, initEmbedsEditorPage } from './pages/embeds.js';
import { initVerificationPage } from './pages/verification.js';
import { initCommandsEditorPage } from './pages/commands.js';
import { initAutoresponderEditorPage } from './pages/autoresponder.js';
import { initSuggestionsPage } from './pages/suggestions.js';
import { initGiveawaysListPage, initGiveawaysEditorPage } from './pages/giveaways.js';
import { initTempVoiceEditorPage } from './pages/tempvoice.js';
import { initStatChannelsEditorPage } from './pages/statchannels.js';
import { initApplicationsListPage, initApplicationsEditorPage } from './pages/applications.js';
import { initModmailPage } from './pages/modmail.js';
import { initReportsPage } from './pages/reports.js';
import { initStarboardEditorPage } from './pages/starboard.js';
import { initPollsListPage, initPollsEditorPage } from './pages/polls.js';
import { initScheduledEditorPage } from './pages/scheduled.js';
import { initBirthdaysPage } from './pages/birthdays.js';
import { initSocialPage } from './pages/social.js';
import { initGalleryPage } from './pages/gallery.js';
import { loadClientStrings } from './lib/i18n.js';
import { upgradeSelects } from './lib/select-menu.js';
import { upgradeColorInputs } from './lib/color-picker.js';
import { initCopyFields } from './lib/copy-field.js';
import { upgradeEmojiInputs } from './lib/emoji-picker.js';
import { applyDataStyles } from './lib/dom.js';
import { initReveal } from './lib/reveal.js';
import { initNavGroups } from './lib/nav-groups.js';
import { initNavScroll } from './lib/nav-scroll.js';
import { initDropdowns } from './lib/dropdown.js';
import { initChangelogBell } from './lib/changelog-bell.js';
import { initAvatarFallback } from './lib/avatar-fallback.js';
import { initHealthCard } from './lib/health.js';
import { initCountingPage } from './pages/counting.js';
import { initSecurityPage } from './pages/security.js';
import { initNudgesPage } from './pages/nudges.js';
import { initGiftPage } from './pages/gift.js';
import { initMusicPage, initMusicPlayer } from './pages/music.js';
import { initMusicLyricsPage } from './pages/music-lyrics.js';
import { initInvitesPage } from './pages/invites.js';
import { initStickyEditorPage } from './pages/sticky.js';
import { initWatchEditorPage } from './pages/watch.js';
import { initMarketplacePages } from './pages/marketplace.js';
import { initQotdPage } from './pages/qotd.js';

/**
 * Entry point of the dashboard.
 *
 * The old version loaded sixteen separate `<script>` files, some of which
 * duplicated the same global functions (`openPremiumPopup` was redefined in
 * three views). Here a single module is loaded and each page initialises only
 * what concerns it: the `init*` functions return immediately when their anchor
 * point is absent from the DOM.
 */
/**
 * Runs a startup step without letting its failure take down the following ones.
 *
 * The steps were called one after another: the first one to throw interrupted
 * `boot()`, and everything that followed was never mounted. A single defect -
 * unexpected data in a `<select>`, a malformed JSON block - was therefore
 * enough to deprive the whole page of its JavaScript. The form then went off in
 * a native submission, which produced a 400 error and left the settings in the
 * URL.
 *
 * The error is reported rather than swallowed: a partially armed dashboard
 * should be visible in the console, not guessed at.
 */
function step(name: string, run: () => void): void {
  try {
    run();
  } catch (error) {
    console.error(`[BILBI] failed at startup: ${name}`, error);
  }
}

/**
 * Exported for `boot.smoke.test.ts`, which mounts it against every rendered
 * page: `step()` swallows a widget's failure by design, and the only way to
 * make that silence loud is to boot the bundle and listen.
 */
export function boot(): void {
  // Before anything else: the components read their labels as they mount.
  step('i18n', loadClientStrings);

  /*
   * Styles the server could not inline, the CSP forbidding a `style` attribute.
   * Early, and before any widget: a quota bar with no fill and a preview with
   * no colour are wrong from the first paint, not after the page settles.
   */
  step('styles', applyDataStyles);

  /*
   * Straight after the styles, and for the same reason: the reveal only starts
   * hiding sections once it is certain it can show them again.
   */
  step('apparitions', initReveal);

  /*
   * The sidebar groups collapse without this: `<details>` does that on its own
   * and the server picks the default. What the bundle adds is the memory of
   * what the reader left open.
   */
  step('groupes de navigation', initNavGroups);

  /*
   * The column's own position, and it must come after the groups: opening or
   * closing one changes the height everything below it sits at, so restoring
   * an offset first would restore it against a list that then moves.
   */
  step('position de la navigation', initNavScroll);

  /*
   * Dropdowns straight after: the language switcher lives on every page,
   * including the ones a signed-out visitor sees. Wiring it behind the page
   * controllers would leave it dead on a phone for as long as any of them
   * takes to fail.
   */
  step('menus déroulants', initDropdowns);

  /*
   * The bell's dot, on every page: the navbar is everywhere, and the answer to
   * "is there anything new" is worth nothing on the one page that carries it.
   */
  step('cloche des nouveautés', initChangelogBell);

  // The navbar is on every page too, and so is the account avatar in it.
  step('avatar de secours', initAvatarFallback);

  /*
   * The health card asks the server what is broken, after the page has painted:
   * the audit reads Discord and the database, and everybody would wait for it.
   */
  step('diagnostic du serveur', initHealthCard);

  /*
   * Replacement of the native fields, before initialising the pages.
   * The browser's `<select>` and `<input type="color">` elements were the cause
   * of several reported defects: an invisible selection on a dark background,
   * horizontal scrolling of the role lists, truncated labels overlapping, and a
   * system palette unrelated to the theme.
   */
  /*
   * The compartments first: a page split into tabs has to be split before the
   * widgets mount inside it, otherwise the components of a hidden tab measure
   * themselves against an element of zero width.
   */
  step('compartiments', initSectionNav);

  step('selects', upgradeSelects);
  step('couleurs', upgradeColorInputs);
  step('emojis', upgradeEmojiInputs);

  // Public pages too, not only the dashboard: the partner page hands out a
  // link and is the reason this exists.
  step('copie', initCopyFields);

  // Mounted on every dashboard page: the module switches and the search live
  // in the shared shell, not only in the gallery.
  step('galerie', initGalleryPage);

  step('paramètres', initSettingsPage);
  step('bienvenue', initWelcomePage);
  step('réactions (liste)', initReactionsListPage);
  step('réactions (édition)', initReactionsEditorPage);
  step('tickets (liste)', initTicketsListPage);
  step('tickets (édition)', initTicketsEditorPage);
  step('bot personnalisé', initCustomBotPage);
  step('facturation', initBillingPage);
  step('journalisation', initLogsPage);
  step('niveaux', initLevelsPage);
  step('modération', initModerationPage);
  step('automod', initAutoModPage);
  step('rôles', initRolesPage);
  step('couleurs', initColorsPage);
  step('automatisations', initAutomationsPage);
  step('réputation', initReputationPage);
  step('économie', initEconomyPage);
  step('sauvegardes', initBackupsPage);
  step('embeds (liste)', initEmbedsListPage);
  step('embeds (édition)', initEmbedsEditorPage);
  step('vérification', initVerificationPage);
  step('commandes (édition)', initCommandsEditorPage);
  step('réponses automatiques (édition)', initAutoresponderEditorPage);
  step('suggestions', initSuggestionsPage);
  step('concours (liste)', initGiveawaysListPage);
  step('concours (édition)', initGiveawaysEditorPage);
  step('vocaux temporaires (édition)', initTempVoiceEditorPage);
  step('modmail', initModmailPage);
  step('signalements', initReportsPage);
  step('candidatures (liste)', initApplicationsListPage);
  step('candidatures (édition)', initApplicationsEditorPage);
  step('salons statistiques (édition)', initStatChannelsEditorPage);
  step('starboard (édition)', initStarboardEditorPage);
  step('sondages (liste)', initPollsListPage);
  step('sondages (édition)', initPollsEditorPage);
  step('messages programmés (édition)', initScheduledEditorPage);
  step('anniversaires', initBirthdaysPage);
  step('alertes sociales', initSocialPage);
  step('compte collectif', initCountingPage);
  step('sécurité', initSecurityPage);
  step('rappels', initNudgesPage);
  step('cadeau', initGiftPage);
  step('musique', initMusicPage);
  step('invitations', initInvitesPage);
  step('messages épinglés', initStickyEditorPage);
  step('surveillance', initWatchEditorPage);
  step('marketplace', initMarketplacePages);
  step('lecteur web', initMusicPlayer);
  step('paroles', initMusicLyricsPage);
  step('question du jour', initQotdPage);

  /*
   * Last, and that placement is the point.
   *
   * Every page above has armed its save bar by now. A tour that threw while
   * mounting would take down whatever came after it, and what comes after a
   * help feature must never be the ability to save (AGENTS §3.5).
   */
  step('visite guidée', initTour);
}

// The script is loaded as `type="module"`, hence deferred: the DOM is ready.
// The check stays useful should the bundle one day be injected differently.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
